import type {
  DeviceType,
  PrintAttemptOutcome,
  PrintDestinationRole,
  PrintDocumentType,
  PrintJobStatus,
  PrinterConnection,
  PrinterProtocol,
  PrinterStatus,
  VersionEnforcement,
} from '@/lib/shared-types';

/**
 * ## The §11 fleet contract, in one file
 *
 * Every path this feature calls and every shape it reads off the wire is
 * declared here and nowhere else, so a backend change that moves a route or
 * renames a field is a one-file edit rather than a hunt through six panels.
 * The screens import `FLEET_ROUTES` and these interfaces; none of them writes a
 * URL string of its own.
 *
 * Read from the backend epic branch (`feat/device-fleet`, ADR-0009):
 *   - `src/modules/devices/devices.controller.ts` + `dto/`
 *   - `src/modules/printing/printing.controller.ts` + `dto/`
 *   - `src/modules/printing/print-bridges.controller.ts` + `dto/bridge.dto.ts`
 *   - the `Printer` / `PrintDestination` / `PrintRoutingRule` / `PrintJob` /
 *     `PrintJobAttempt` / `PrintBridge` models in `prisma/schema.prisma`
 *
 * The bridge's own routes (`/print-bridge/jobs/claim` and friends) are
 * deliberately absent: they authenticate with a bridge token rather than a
 * staff session, and no browser ever calls them.
 *
 * The types are hand-written mirrors rather than generated, matching how
 * `@/lib/shared-types` already mirrors the Prisma enums. They describe what the
 * services actually `select`, which is narrower than the models in several
 * places — a spool row carries no `payload`, and a bridge never carries its
 * `tokenHash`.
 */
export const FLEET_ROUTES = {
  devices: '/devices',
  device: (id: string) => `/devices/${id}`,
  deviceDiagnostics: (id: string) => `/devices/${id}/diagnostics`,
  devicePairing: (id: string) => `/devices/${id}/pairing`,
  deviceRevoke: (id: string) => `/devices/${id}/revoke`,
  createPairing: '/devices/pairing',
  fleetHealth: '/devices/fleet/health',
  versionPolicies: '/devices/version-policies',

  printers: '/printing/printers',
  printer: (id: string) => `/printing/printers/${id}`,
  printerTestPrint: (id: string) => `/printing/printers/${id}/test-print`,

  destinations: '/printing/destinations',
  destination: (id: string) => `/printing/destinations/${id}`,

  routingRules: '/printing/routing-rules',
  routingRule: (id: string) => `/printing/routing-rules/${id}`,

  jobs: '/printing/jobs',
  job: (id: string) => `/printing/jobs/${id}`,
  jobRetry: (id: string) => `/printing/jobs/${id}/retry`,
  jobReprint: (id: string) => `/printing/jobs/${id}/reprint`,
  jobCancel: (id: string) => `/printing/jobs/${id}/cancel`,

  bridges: '/print-bridges',
  bridge: (id: string) => `/print-bridges/${id}`,
  bridgeToken: (id: string) => `/print-bridges/${id}/token`,
  bridgeRevoke: (id: string) => `/print-bridges/${id}/revoke`,
} as const;

/**
 * The permission keys the API enforces on the routes above, so a screen never
 * offers a button that is guaranteed to answer 403. Mirrors
 * `src/common/constants/permissions.ts` in the backend.
 */
export const FLEET_PERMISSIONS = {
  devicesView: 'devices.view',
  devicesRegister: 'devices.register',
  devicesRevoke: 'devices.revoke',
  devicesDiagnose: 'devices.diagnose',
  devicesManageVersions: 'devices.manage_versions',

  printersView: 'printers.view',
  printersCreate: 'printers.create',
  printersUpdate: 'printers.update',
  printersDelete: 'printers.delete',
  printersTestPrint: 'printers.test_print',

  jobsView: 'print_jobs.view',
  jobsRetry: 'print_jobs.retry',
  jobsReprint: 'print_jobs.reprint',
  jobsCancel: 'print_jobs.cancel',

  bridgesView: 'print_bridges.view',
  bridgesEnroll: 'print_bridges.enroll',
  bridgesRotate: 'print_bridges.rotate',
  bridgesRevoke: 'print_bridges.revoke',
} as const;

/**
 * The stable domain error codes printing answers with, from
 * `src/modules/printing/print-errors.ts`. They are codes rather than prose
 * because the dashboard acts on them: a client that string-matches a message
 * breaks the first time the message is translated, and each refusal has a
 * different button behind it.
 */
export const PRINT_ERROR_CODES = {
  routeNotConfigured: 'PRINT_ROUTE_NOT_CONFIGURED',
  destinationHasNoPrinter: 'PRINT_DESTINATION_HAS_NO_PRINTER',
  jobNotPrinted: 'PRINT_JOB_NOT_PRINTED',
  reprintKeyRequired: 'PRINT_REPRINT_KEY_REQUIRED',
  jobNotRetryable: 'PRINT_JOB_NOT_RETRYABLE',
  leaseStale: 'PRINT_LEASE_STALE',
  printerNeedsBridge: 'PRINTER_NEEDS_BRIDGE',
  versionPolicyWouldBlock: 'DEVICE_VERSION_POLICY_WOULD_BLOCK',
} as const;

/** The code on a refusal, when the API sent one. */
export function errorCode(error: unknown): string | null {
  const code = (error as { response?: { data?: { code?: unknown } } })?.response?.data?.code;
  return typeof code === 'string' ? code : null;
}

/** `affected` on a `DEVICE_VERSION_POLICY_WOULD_BLOCK` refusal — how many die. */
export function refusalAffected(error: unknown): number | null {
  const n = (error as { response?: { data?: { affected?: unknown } } })?.response?.data?.affected;
  return typeof n === 'number' ? n : null;
}

// ---------------------------------------------------------------- shared refs

export interface BranchRef {
  id: string;
  name: string;
  code?: string | null;
}

export interface PrinterRef {
  id: string;
  name: string;
  code: string;
  lastStatus?: PrinterStatus;
}

export interface DestinationRef {
  id: string;
  name: string;
  code: string;
  role: PrintDestinationRole;
}

export interface BridgeRef {
  id: string;
  name: string;
  isActive?: boolean;
  lastSeenAt?: string | null;
}

// ------------------------------------------------------------------- devices

/**
 * `DeviceHealth` is **derived at read time and never stored** (ADR-0009 §6). A
 * stored column goes stale the moment a device stops beating, and the failure
 * mode is the worst available: a dashboard confidently reporting HEALTHY for a
 * tablet that has been in a drawer since Tuesday.
 *
 * The five states are deliberately not a subset of `isActive`: that boolean is
 * worn by a device awaiting its pairing code AND by one revoked after a theft,
 * and one of those wants a code typed into it while the other wants a police
 * report.
 */
export type DeviceHealth = 'HEALTHY' | 'DEGRADED' | 'OFFLINE' | 'REVOKED' | 'NEVER_SEEN';

/**
 * "We do not know" and "it is fine" are different answers. Folding `UNKNOWN`
 * into `SUPPORTED` is how a fleet of unreported devices comes to look fully
 * patched.
 */
export type VersionCompliance = 'SUPPORTED' | 'OUTDATED' | 'UNSUPPORTED' | 'UNKNOWN';

export interface VersionVerdict {
  compliance: VersionCompliance;
  /** True only when the policy says BLOCK *and* the version is below the floor. */
  blocked: boolean;
  reportedVersion: string | null;
  minSupportedVersion: string | null;
  recommendedVersion: string | null;
}

/**
 * A row of `GET /devices`.
 *
 * Structurally a superset of `PairedDevice` in
 * `Pages/Settings/components/DeviceFormDialog`, on purpose: the enrolment and
 * pairing-code dialogs already exist there and are reused from here rather than
 * rewritten, so a row read by this screen has to be a row that dialog accepts.
 *
 * Notably absent is any `health` field. `DevicesService.findAll` returns the raw
 * row and does not derive one — see `fleetHealth.ts` for why this screen has to,
 * and ADR-0009 §6 for why nothing stores it.
 */
export interface FleetDevice {
  id: string;
  name: string;
  type: DeviceType;
  /** Null for a row still awaiting pairing — it holds a hashed ticket, not an identity. */
  fingerprint: string | null;
  branchId: string | null;
  /** `DevicesService` selects `{ id, name, code }`, and `code` is non-null there. */
  branch?: { id: string; name: string; code: string } | null;
  isActive: boolean;
  lastSeenAt: string | null;
  createdAt: string;
  appVersion?: string | null;
  platform?: string | null;
  osVersion?: string | null;
  revokedAt?: string | null;
  revokedReason?: string | null;
  user?: { id: string; firstName: string; lastName: string | null } | null;
  /** The pairing sentinel, surfaced by `DevicesService.present()`. */
  pairing?: { status: 'PENDING' | 'PAIRED' | 'EXPIRED'; expiresAt: string | null };
}

/** `GET /devices/fleet/health` — counts, not a list. */
export interface FleetHealthRollup {
  branchId: string | null;
  observedAt: string;
  devices: {
    total: number;
    byHealth: Record<DeviceHealth, number>;
    unsupported: Array<{ id: string; name: string; version: string | null }>;
  };
  printers: { total: number; inTrouble: number };
  bridges: { total: number; stale: number };
  jobs: { queued: number; failed: number; dead: number };
}

/** `GET /devices/:id/diagnostics`. Carries no document payloads by design. */
export interface DeviceDiagnostics {
  device: {
    id: string;
    name: string;
    type: DeviceType;
    branch: BranchRef | null;
    /** Blanked for a row still awaiting pairing — it holds a hashed ticket. */
    fingerprint: string | null;
    isActive: boolean;
    revokedAt: string | null;
    revokedReason: string | null;
    lastSeenAt: string | null;
    platform: string | null;
    osVersion: string | null;
    pinLockedUntil: string | null;
    pinAttempts: number;
  };
  health: DeviceHealth;
  awaitingPairing: boolean;
  version: VersionVerdict;
  bridge: {
    id: string;
    name: string;
    isActive: boolean;
    agentVersion: string | null;
    lastSeenAt: string | null;
    revokedAt: string | null;
    printers: Array<
      PrinterRef & { lastStatusAt: string | null; lastStatusDetail: string | null }
    >;
  } | null;
  recentPrintFailures: Array<{
    id: string;
    documentType: PrintDocumentType;
    status: PrintJobStatus;
    attempts: number;
    lastError: string | null;
    failedAt: string | null;
    printerId: string | null;
  }>;
}

export interface DeviceVersionPolicy {
  id: string;
  deviceType: DeviceType;
  minSupportedVersion: string;
  recommendedVersion: string | null;
  enforcement: VersionEnforcement;
  notes: string | null;
  updatedAt: string;
}

export interface UpsertVersionPolicyBody {
  deviceType: DeviceType;
  minSupportedVersion: string;
  recommendedVersion?: string;
  enforcement?: VersionEnforcement;
  notes?: string;
  /** Apply a BLOCK policy even though live devices already fail it. */
  force?: boolean;
}

// ------------------------------------------------------------------ printers

export interface FleetPrinter {
  id: string;
  branchId: string;
  name: string;
  code: string;
  connection: PrinterConnection;
  protocol: PrinterProtocol;
  bridgeId: string | null;
  address: string | null;
  port: number | null;
  paperWidthMm: number;
  charactersPerLine: number;
  isActive: boolean;
  lastStatus: PrinterStatus;
  lastStatusAt: string | null;
  lastStatusDetail: string | null;
  branch?: BranchRef;
  bridge?: BridgeRef | null;
}

export interface CreatePrinterBody {
  branchId: string;
  name: string;
  code: string;
  connection?: PrinterConnection;
  protocol?: PrinterProtocol;
  bridgeId?: string;
  address?: string;
  port?: number;
  paperWidthMm?: number;
  charactersPerLine?: number;
}

/** The branch is where a printer physically is, and hardware does not move by PATCH. */
export type UpdatePrinterBody = Partial<Omit<CreatePrinterBody, 'branchId' | 'code'>> & {
  isActive?: boolean;
};

/** The three connections only an agent inside the venue can reach. */
export const BRIDGE_CONNECTIONS: PrinterConnection[] = [
  'BRIDGE_LAN',
  'BRIDGE_USB',
  'BRIDGE_BLUETOOTH',
];

export function connectionNeedsBridge(connection: PrinterConnection): boolean {
  return BRIDGE_CONNECTIONS.includes(connection);
}

// -------------------------------------------------------------- destinations

export interface FleetDestination {
  id: string;
  branchId: string;
  name: string;
  code: string;
  role: PrintDestinationRole;
  printerId: string | null;
  fallbackPrinterId: string | null;
  copies: number;
  isActive: boolean;
  printer?: PrinterRef | null;
  fallbackPrinter?: PrinterRef | null;
}

export interface CreateDestinationBody {
  branchId: string;
  name: string;
  code: string;
  role: PrintDestinationRole;
  printerId?: string;
  fallbackPrinterId?: string;
  copies?: number;
}

export type UpdateDestinationBody = Partial<Omit<CreateDestinationBody, 'branchId' | 'code'>> & {
  isActive?: boolean;
};

/**
 * A rule never re-decides which station makes a dish — the kitchen already did
 * that, and `OrderItem.stationId` stays the only authority. A rule takes the
 * station the kitchen chose and answers the narrower question of which paper
 * that station's ticket comes out on. Every criterion is nullable, and null
 * means "any".
 */
export interface FleetRoutingRule {
  id: string;
  branchId: string;
  destinationId: string;
  documentType: PrintDocumentType | null;
  stationId: string | null;
  categoryId: string | null;
  fulfillmentType: 'DINE_IN' | 'TAKEAWAY' | 'DELIVERY' | null;
  priority: number;
  isActive: boolean;
  destination?: DestinationRef;
  station?: { id: string; name: string; code?: string | null } | null;
  category?: { id: string; name: string } | null;
}

export interface CreateRoutingRuleBody {
  destinationId: string;
  documentType?: PrintDocumentType;
  stationId?: string;
  categoryId?: string;
  fulfillmentType?: 'DINE_IN' | 'TAKEAWAY' | 'DELIVERY';
  priority?: number;
}

export type UpdateRoutingRuleBody = Partial<Omit<CreateRoutingRuleBody, 'destinationId'>> & {
  isActive?: boolean;
};

// ----------------------------------------------------------------- the spool

/**
 * A row of `GET /printing/jobs`.
 *
 * The rendered document is deliberately **not** here: a spool list is a screen
 * a whole shift can see, and a kitchen ticket carries a guest's order.
 */
export interface PrintJobRow {
  id: string;
  branchId: string;
  destinationId: string;
  printerId: string | null;
  bridgeId: string | null;
  documentType: PrintDocumentType;
  status: PrintJobStatus;
  attempts: number;
  maxAttempts: number;
  priority: number;
  lastError: string | null;
  sourceType: string | null;
  sourceId: string | null;
  isReprint: boolean;
  reprintOfId: string | null;
  containsCustomerData: boolean;
  queuedAt: string;
  availableAt: string;
  printedAt: string | null;
  failedAt: string | null;
  leaseExpiresAt: string | null;
  destination?: DestinationRef;
  printer?: PrinterRef | null;
}

export interface PrintJobAttempt {
  attempt: number;
  outcome: PrintAttemptOutcome;
  detail: string | null;
  printerStatus: PrinterStatus | null;
  bridgeId: string | null;
  printerId: string | null;
  createdAt: string;
}

/**
 * `GET /printing/jobs/:id` — one job with every delivery attempt against it.
 *
 * The rendered document is `omit`ted by the service, here as in the list: a
 * diagnostics page is read over shoulders, and a kitchen ticket carries a
 * guest's order. `payloadScrubbedAt` is what replaces it, and it answers the
 * question an operator actually has — whether a reprint is still possible.
 */
export interface PrintJobDetail extends PrintJobRow {
  /** Set once the retention sweep took the document off a terminal job. */
  payloadScrubbedAt?: string | null;
  bridge?: (BridgeRef & { lastSeenAt: string | null }) | null;
  attemptLog: PrintJobAttempt[];
  reprints: Array<{ id: string; status: PrintJobStatus; queuedAt: string }>;
}

export interface ListJobsQuery {
  branchId?: string;
  destinationId?: string;
  printerId?: string;
  status?: PrintJobStatus;
  documentType?: PrintDocumentType;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// ------------------------------------------------------------------- bridges

export interface FleetBridge {
  id: string;
  branchId: string;
  name: string;
  deviceId: string | null;
  agentVersion: string | null;
  lastSeenAt: string | null;
  isActive: boolean;
  revokedAt: string | null;
  revokedReason: string | null;
  tokenIssuedAt: string | null;
  tokenRotatedAt: string | null;
  createdAt: string;
  printers: PrinterRef[];
}

/** Enrolment and rotation both answer a token that is shown once and never again. */
export interface IssuedBridgeToken {
  bridge?: { id: string; name: string; branchId: string; deviceId: string | null; isActive: boolean };
  bridgeId?: string;
  token: string;
}
